// The company's side of a credential access request: which of the requested company files the
// approver chose to share, and what to tell them when a decision is refused. The server re-checks
// everything here - a ticked file it was not asked for, a file ticked by anyone but the company
// admin, or an approval that shares nothing is refused there.

import React, { useState } from 'react';
import { Button, Checkbox } from 'antd';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { COMPANY_FILE_KINDS } from './companyFileChoices';

// The longest a decision can be held back: the length of the server's rate-limit window.
const MAX_WAIT_MINUTES = 15;

export const requestedCompanyFiles = request => {
  const requested = Array.isArray(request?.requestedFiles) ? request.requestedFiles : [];
  return COMPANY_FILE_KINDS.filter(kind => requested.includes(kind));
};

// Approving shares the requested verified fields plus the ticked files, so it shares nothing when
// neither exists.
export const approvalWouldShare = ({ request, tickedFiles = [] }) =>
  (Array.isArray(request?.requestedFields) && request.requestedFields.length > 0) || tickedFiles.length > 0;

// `consentId` is the approver's saved share consent; the server refuses an approval without one.
export const approvalPayload = (requestId, tickedFiles = [], consentId = null) => ({
  requestId,
  ...(tickedFiles.length > 0 ? { files: [...tickedFiles] } : {}),
  ...(consentId ? { consentId } : {}),
});

// A 429's `retryAfter` is the moment (epoch seconds) the limit window resets, not a wait.
const minutesUntil = (retryAfter, now) => {
  if (typeof retryAfter !== 'number' || !Number.isFinite(retryAfter)) return null;
  const minutes = Math.ceil((retryAfter * 1000 - now) / 60000);
  return Math.min(MAX_WAIT_MINUTES, Math.max(1, minutes));
};

export const decisionErrorMessage = (error, { intl, now = Date.now(), fallback } = {}) => {
  if (error?.status === 429) {
    const minutes = minutesUntil(error.retryAfter, now);
    return minutes === null
      ? intl.formatMessage({ id: 'CredentialApproval.tooManyDecisionsLater' })
      : intl.formatMessage({ id: 'CredentialApproval.tooManyDecisions' }, { minutes });
  }
  return error?.error || error?.details || error?.message || fallback;
};

// The files a request asked for. Only the company admin may share a company file, so only they get
// tick boxes, all unticked to start; anyone else sees the files with a note saying so.
export const CredentialRequestFileChoices = ({ files, isCompanyAdmin, ticked, onChange }) => {
  if (!files || files.length === 0) return null;
  const toggle = kind =>
    onChange(ticked.includes(kind) ? ticked.filter(k => k !== kind) : files.filter(k => k === kind || ticked.includes(k)));

  return (
    <div style={{ marginTop: '12px' }}>
      <h4 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '14px' }}>
        <FormattedMessage id="CredentialApproval.filesHeading" />
      </h4>
      {files.map(kind => (
        <div key={kind} style={{ padding: '4px 0', color: '#333', fontSize: '14px' }}>
          {isCompanyAdmin ? (
            <Checkbox checked={ticked.includes(kind)} onChange={() => toggle(kind)}>
              <FormattedMessage id={`CompanyFile.${kind}`} />
            </Checkbox>
          ) : (
            <>
              <FormattedMessage id={`CompanyFile.${kind}`} />
              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#8c8c8c' }}>
                <FormattedMessage id="CredentialApproval.adminOnly" />
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

const fieldLabel = field =>
  field
    .replace(/[-_]/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();

// The body of the approval dialog. A request for company files alone names no document and no
// fields, so those headings are shown only when there are fields. `children` is the file choices.
export const CredentialRequestDetails = ({ request, children }) => {
  const fields = Array.isArray(request.requestedFields) ? request.requestedFields : [];
  const hasFiles = requestedCompanyFiles(request).length > 0;

  return (
    <div style={{ padding: '8px 0' }}>
      <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#484848' }}>
        <strong>{request.requesterName}</strong>
        {request.requesterCompanyName ? ` from ${request.requesterCompanyName}` : ''}
        {' '}has requested you to share your credentials.
      </p>

      <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px', margin: '16px 0' }}>
        {fields.length > 0 && (
          <>
            <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>Document: {request.documentType}</h4>
            <h4 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '14px' }}>Requested Fields:</h4>
            <ul style={{ margin: '0', paddingLeft: '20px' }}>
              {fields.map((field, index) => (
                <li key={index} style={{ padding: '4px 0', color: '#333', fontSize: '14px' }}>
                  {fieldLabel(field)}
                </li>
              ))}
            </ul>
          </>
        )}
        {children}
        {request.message && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>
            <p style={{ fontSize: '14px', color: '#666', margin: '0' }}><strong>Message:</strong></p>
            <p style={{ fontSize: '14px', color: '#333', margin: '4px 0 0 0' }}>{request.message}</p>
          </div>
        )}
      </div>

      <div style={{ background: '#d1ecf1', borderLeft: '4px solid #0e9941', padding: '12px 16px', borderRadius: '4px', marginTop: '16px' }}>
        <p style={{ fontSize: '13px', lineHeight: '1.5', margin: '0', color: '#0c5460' }}>
          <strong>Security Notice:</strong> Only the specific fields listed above will be shared. The requester will receive a one-time-use encrypted link that expires in 48 hours.
        </p>
        {hasFiles && (
          <p style={{ fontSize: '13px', lineHeight: '1.5', margin: '8px 0 0 0', color: '#0c5460' }}>
            <FormattedMessage id="CredentialApproval.fileNotice" />
          </p>
        )}
      </div>
    </div>
  );
};

// The approval dialog's footer. Only the company admin decides a request made of the company, so
// only they get Decline and Share; anyone else is told so and can only close the dialog. The server
// refuses a decision from anyone but the admin either way.
export const CredentialRequestActions = ({ isCompanyAdmin, loading, shareDisabled, onDecline, onShare, onClose }) =>
  isCompanyAdmin
    ? [
        <Button key="reject" onClick={onDecline} loading={loading} disabled={loading}>
          Decline
        </Button>,
        <Button key="approve" type="primary" onClick={onShare} loading={loading} disabled={loading || shareDisabled}>
          Share Credentials
        </Button>,
      ]
    : [
        <span key="note" style={{ marginRight: '12px', fontSize: '13px', color: '#8c8c8c' }}>
          <FormattedMessage id="CredentialApproval.adminOnlyRespond" />
        </span>,
        <Button key="close" onClick={onClose}>
          <FormattedMessage id="CredentialApproval.close" />
        </Button>,
      ];

// Whether an approval's answer says its email to the requester did not go. An answer that says
// nothing about the email - from a request by SMS only, or an older server - is not a failure.
export const approvalEmailFailed = response => response?.emailSent === false;

// Why a resend was refused: a link already used or expired, too many tries, or not allowed.
export const resendRefusalMessage = (error, { intl }) => {
  if (error?.status === 429) return decisionErrorMessage(error, { intl });
  if (error?.status === 410) return intl.formatMessage({ id: 'CredentialApproval.resendLinkNoLongerValid' });
  return intl.formatMessage({ id: 'CredentialApproval.resendNotAllowed' });
};

const NOTICE_MESSAGE = {
  failed: 'CredentialApproval.emailNotSent',
  resending: 'CredentialApproval.emailNotSent',
  resendFailed: 'CredentialApproval.resendFailed',
  sent: 'CredentialApproval.emailResent',
};

// Shown in the approval dialog when access was approved but the email to the requester did not go.
// `status` is 'failed', 'resending', 'resendFailed', 'refused' or 'sent'; `refusal` is the text shown
// in place of the status's own, and Resend is offered unless the resend was refused or has gone.
export const ApprovalEmailNotice = ({ status, refusal, onResend }) => {
  const offerResend = status !== 'refused' && status !== 'sent';
  const resending = status === 'resending';
  return (
    <div style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107', padding: '12px 16px', borderRadius: '4px', marginTop: '16px' }}>
      <p style={{ fontSize: '14px', lineHeight: '1.5', margin: '0', color: '#856404' }} role="status">
        {refusal || <FormattedMessage id={NOTICE_MESSAGE[status] || NOTICE_MESSAGE.failed} />}
      </p>
      {offerResend && (
        <Button style={{ marginTop: '8px' }} onClick={onResend} loading={resending} disabled={resending}>
          <FormattedMessage id={resending ? 'CredentialApproval.resendingEmail' : 'CredentialApproval.resendEmail'} />
        </Button>
      )}
    </div>
  );
};

// The notice together with the resend it offers. `onResend(requestId)` sends the email again and
// resolves the server's answer; `onSent` is called once it has gone, so the dialog can close.
export const ApprovalEmailResend = ({ requestId, onResend, onSent }) => {
  const intl = useIntl();
  const [state, setState] = useState({ status: 'failed', refusal: null });

  const resend = async () => {
    setState({ status: 'resending', refusal: null });
    try {
      const response = await onResend(requestId);
      if (response?.emailSent) {
        setState({ status: 'sent', refusal: null });
        if (onSent) onSent();
      } else {
        setState({ status: 'resendFailed', refusal: null });
      }
    } catch (error) {
      if (error?.status === 429) {
        setState({ status: 'resendFailed', refusal: resendRefusalMessage(error, { intl }) });
      } else if ([403, 404, 409, 410].includes(error?.status)) {
        setState({ status: 'refused', refusal: resendRefusalMessage(error, { intl }) });
      } else {
        setState({ status: 'resendFailed', refusal: null });
      }
    }
  };

  return <ApprovalEmailNotice status={state.status} refusal={state.refusal} onResend={resend} />;
};
